import { Col, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";

import { PROGRAM_FIELDS, ProgramFieldDefinition } from "../../../../constants/ripeningProgramFields";
import {
    STORE_BLOCK_FIELDS,
    StoreBlockFieldKey,
    storeInjectsN2,
    StoreValues,
} from "../../../../constants/ripeningStorePrograms";

interface StoreSettingsFieldsProps {
    values: StoreValues;
    onChange?: (key: StoreBlockFieldKey, value: number | undefined) => void;
    // * Fields that can be changed; all of them when left out. Without onChange nothing can.
    editable?: (key: StoreBlockFieldKey) => boolean;
    // * Show the room's own field name as a tooltip (program creator only).
    showKeys?: boolean;
    idPrefix?: string;
}

/** The store settings as one compact block: temperature, ventilation and the gases. */
export default function StoreSettingsFields({ values, onChange, editable, showKeys = false, idPrefix = "store" }: StoreSettingsFieldsProps) {
    const canEdit = (key: StoreBlockFieldKey) => !!onChange && (editable ? editable(key) : true);
    const parse = (value: string) => (value.trim() === "" || !Number.isFinite(Number(value)) ? undefined : Number(value));

    const renderInput = (key: StoreBlockFieldKey, label: string) => {
        const definition: ProgramFieldDefinition = PROGRAM_FIELDS[key];
        const id = `${idPrefix}-${key}`;
        const current = values[key];
        if (!canEdit(key)) {
            const option = definition.options?.find((item) => item.value === current);
            const text = current === undefined ? "-" : `${option?.label ?? current}${!option && definition.unit ? ` ${definition.unit}` : ""}`;
            return <div id={id} className="fw-semibold" data-testid={id}>{text}</div>;
        }
        if (definition.kind === "select") {
            const known = definition.options?.some((option) => option.value === current);
            return (
                <Input
                    id={id}
                    bsSize="sm"
                    type="select"
                    value={current ?? ""}
                    title={showKeys ? definition.key : undefined}
                    onChange={(event) => onChange?.(key, parse(event.target.value))}
                >
                    <option value="">—</option>
                    {current !== undefined && !known && <option value={current}>{current}</option>}
                    {definition.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Input>
            );
        }
        return (
            <InputGroup size="sm" className="flex-nowrap">
                <Input
                    id={id}
                    type="number"
                    step="any"
                    min={definition.min}
                    max={definition.max}
                    value={current ?? ""}
                    title={showKeys ? definition.key : undefined}
                    aria-label={label}
                    onChange={(event) => onChange?.(key, parse(event.target.value))}
                />
                {definition.unit && <InputGroupText>{definition.unit}</InputGroupText>}
            </InputGroup>
        );
    };

    return (
        <Row className="gx-3 gy-2" data-testid="store-settings">
            {STORE_BLOCK_FIELDS.map(([key, label]) => (
                <Col xs={6} md={3} key={key}>
                    <Label for={`${idPrefix}-${key}`} className="small mb-1">
                        <span title={showKeys ? PROGRAM_FIELDS[key].key : undefined}>{label}</span>
                    </Label>
                    {renderInput(key, label)}
                    {key === "Store_Spv_C2H4" && (
                        <div className="small text-muted mt-1" data-testid={`${idPrefix}-ethylene-dosing`} title={showKeys ? PROGRAM_FIELDS.Store_Mode_C2H4.key : undefined}>
                            dosing {Number(values.Store_Spv_C2H4) > 0 ? "on" : "off"} (on above 0)
                        </div>
                    )}
                    {key === "Store_Mode_N2" && storeInjectsN2(values) && (
                        <div className="small fw-semibold text-warning-emphasis mt-1" data-testid={`${idPrefix}-low-oxygen`}>
                            {"⚠ "}Low oxygen
                        </div>
                    )}
                </Col>
            ))}
        </Row>
    );
}
